/**
 * Lobby panel (DESIGN 6.6 step 3): room code with a copy-link button, the
 * player list with host badge and ready marks, the mode picker (host
 * picks Waves or Story; everyone sees the choice), the ready toggle, the Start
 * button (host only, enabled once every other connected player is ready;
 * the server counts the host's Start press as their own ready), Leave, and
 * a "How to play" card. It sits on the right over the arena so players can
 * walk around and cast at the dummies while they wait: the root never
 * captures pointer events outside the panel, and no control keeps focus.
 */

import type { Difficulty, LobbyPlayer, ModeId, RoomPhase } from '@shared/index.js';
import { DIFFICULTIES, DIFFICULTY, MAX_PLAYERS, MODES, MODE_IDS, isPvp, playerColorHex } from '@shared/index.js';
import { TEAMS } from '../palette.js';
import type { ClientState } from '../state/clientState.js';
import type { Socket } from '../net/socket.js';
import type { ViewRoom } from '../state/viewModel.js';
import { type Screen, button, clear, copyText, el, keycap, setText, storageGet, storageSet, toast } from './dom.js';
import { STORAGE_KEYS, roomLink, startState } from './helpers.js';

export function mount(root: HTMLElement, state: ClientState, socket: Socket): Screen {
  const codeEl = el('span', { class: 'room-code', title: 'Room code' });
  const copyBtn = button('Copy link', () => { void copyLink(); }, 'btn btn-ghost btn-sm');
  const list = el('ul', { class: 'players' });
  const readyBtn = button('Ready up', () => toggleReady(), 'btn');
  const startBtn = button('Start match', () => socket.send({ t: 'start' }), 'btn btn-primary');
  const startHint = el('div', { class: 'hint' });
  const leaveBtn = button('Leave', () => leave(), 'btn btn-ghost btn-sm');
  // What the room plays: the host picks, everyone sees it (modes.ts).
  const modeBtns = new Map<ModeId, HTMLButtonElement>();
  for (const id of MODE_IDS) {
    const b = button(el('span', { class: 'mode-inner' }, el('span', { class: 'mode-name' }, MODES[id].name), el('span', { class: 'mode-blurb' }, MODES[id].blurb)),
      () => socket.send({ t: 'mode', m: id }), 'btn btn-mode');
    modeBtns.set(id, b);
  }
  const modeHint = el('div', { class: 'hint' });
  const modeRow = el('div', { class: 'lobby-mode' }, el('div', { class: 'label' }, 'Mode'), el('div', { class: 'mode-btns' }, ...modeBtns.values()), modeHint);
  // How hard: the host picks (difficulty.ts); capture the flag has no enemies, so the row hides there.
  const diffBtns = new Map<Difficulty, HTMLButtonElement>();
  for (const d of DIFFICULTIES) diffBtns.set(d, button(DIFFICULTY[d].label, () => socket.send({ t: 'difficulty', d }), `btn btn-sm btn-diff btn-diff-${d}`));
  const diffHint = el('div', { class: 'hint' });
  const diffRow = el('div', { class: 'lobby-diff' }, el('div', { class: 'label' }, 'Difficulty'), el('div', { class: 'diff-btns' }, ...diffBtns.values()), diffHint);
  // Collapsed, the room is a slim tab on the left edge; it pops the panel out on click/tap.
  const tabCode = el('span', { class: 'lobby-tab-code' });
  const tabInfo = el('span', { class: 'lobby-tab-info' });
  // A keyboard click (detail 0) moves focus to the control that replaces the one pressed, so Tab carries on from there.
  const tab = button(el('span', { class: 'lobby-tab-inner' }, tabCode, tabInfo, el('span', { class: 'lobby-tab-arrow' }, '›')), (ev) => setOpen(true, ev.detail === 0), 'btn lobby-tab');
  tab.title = 'Room: players, ready, start';
  const hideBtn = button('‹', (ev) => setOpen(false, ev.detail === 0), 'btn btn-ghost btn-sm lobby-hide');
  hideBtn.title = 'Hide the room panel';
  hideBtn.setAttribute('aria-label', 'Hide the room panel');

  const howTo = el('div', { class: 'card howto' },
    el('h3', null, 'How to play'),
    el('ul', null,
      el('li', null, keycap('Q W E R'), ' ', keycap('A S D F'), ' queue up to 5 elements. Opposites cancel; ', keycap('Q'), '+', keycap('F'), ' = Steam, ', keycap('Q'), '+', keycap('R'), ' = Ice.'),
      el('li', null, 'The same queue casts four ways: ', keycap('LMB'), ' Force (beam, spray, boulder), ', keycap('Shift + LMB'), ' Area (nova around you), ', keycap('Middle click'), ' Self (heal, armor, aura), ', keycap('Shift + Right click'), ' or ', keycap('V'), ' Staff: imbues it if you have elements queued, swings it if not.'),
      el('li', null, 'Walk: hold ', keycap('RMB'), ' toward the cursor, or use the arrow keys.'),
      el('li', null, 'Wet + lightning = x2. Wet + cold = frozen (x3). Fire thaws and dries. Wet yourself and press ', keycap('A'), ' at your own risk.'),
      el('li', null, 'Friendly fire is always on. ', keycap('W A'), ' then ', keycap('Space'), ' revives a fallen friend.'),
      el('li', null, 'Practice on the dummies while you wait. ', keycap('Tab'), ' walks these buttons until the match starts, then it opens the spellbook while held (the Book button works any time); ', keycap('O'), ' or the gear rebinds any control.'),
    ),
  );

  const panel = el('aside', { class: 'lobby-panel', 'aria-label': 'Lobby' },
    el('div', { class: 'lobby-head' },
      el('div', null, el('div', { class: 'label' }, 'Room'), codeEl),
      copyBtn,
      hideBtn,
    ),
    list,
    modeRow,
    diffRow,
    el('div', { class: 'lobby-actions' }, readyBtn, startBtn, startHint),
    howTo,
    el('div', { class: 'lobby-foot' }, leaveBtn, el('span', { class: 'muted' }, keycap('Enter'), ' chat ', keycap('M'), ' mute')),
  );
  clear(root);
  root.classList.add('lobby');
  root.appendChild(panel);
  root.appendChild(tab);

  let open = storageGet(STORAGE_KEYS.lobbyOpen) === '1';
  function setOpen(o: boolean, refocus = false): void {
    open = o;
    storageSet(STORAGE_KEYS.lobbyOpen, o ? '1' : '0');
    panel.hidden = !o;
    tab.hidden = o;
    if (refocus) (o ? hideBtn : tab).focus({ preventScroll: true });
  }
  setOpen(open);

  let lastRoom: ViewRoom | null = null;
  let lastPhase: RoomPhase | null = null;
  let lastPid = -1;
  let suppressed = false;

  async function copyLink(): Promise<void> {
    const code = state.roomCode;
    if (!code) return;
    const link = roomLink(code, window.location);
    toast((await copyText(link)) ? 'Link copied. Send it to your friends.' : `Share this link: ${link}`, 'success');
  }

  function toggleReady(): void {
    const me = state.room?.players.find((p) => p.id === state.pid);
    if (!me) return;
    socket.send({ t: 'ready', ready: !me.ready });
  }

  function leave(): void {
    socket.send({ t: 'leave' });
    state.leaveRoom();
    window.history.replaceState(null, '', window.location.pathname);
  }

  function playerRow(p: LobbyPlayer, me: boolean): HTMLElement {
    const dot = el('span', { class: 'dot' });
    dot.style.setProperty('--swatch', playerColorHex(p.color));
    // team modes: the player's side, and for yourself a button to change it
    let team: HTMLElement | null = null;
    if (p.team !== undefined) {
      const side = TEAMS[p.team];
      team = el('span', { class: 'badge badge-team' }, side.name);
      team.style.setProperty('--team', side.color);
    }
    const other = p.team === 0 ? 1 : 0;
    const swap = me && p.team !== undefined
      ? button(`Join ${TEAMS[other].name}`, () => socket.send({ t: 'team', team: other }), 'btn btn-small btn-team')
      : null;
    return el('li', { class: `player${p.connected ? '' : ' player-away'}${me ? ' player-me' : ''}` },
      dot,
      el('span', { class: 'name' }, p.name, me ? el('span', { class: 'muted' }, ' (you)') : null),
      team,
      swap,
      p.host ? el('span', { class: 'badge badge-host' }, 'Host') : null,
      !p.connected
        ? el('span', { class: 'badge badge-away' }, 'Reconnecting')
        : el('span', { class: `badge ${p.ready ? 'badge-ready' : 'badge-wait'}` }, p.ready ? 'Ready' : 'Not ready'),
    );
  }

  function render(room: ViewRoom): void {
    setText(codeEl, room.code);
    setText(tabCode, room.code);
    const ready = room.players.filter((p) => p.ready).length;
    setText(tabInfo, `${room.players.length}/${MAX_PLAYERS} · ${ready} ready${isPvp(room.mode) ? '' : ` · ${DIFFICULTY[room.difficulty].label}`}`);
    clear(list);
    for (const p of room.players) list.appendChild(playerRow(p, p.id === state.pid));
    for (let i = room.players.length; i < MAX_PLAYERS; i++) {
      list.appendChild(el('li', { class: 'player player-empty' }, el('span', { class: 'dot dot-empty' }), el('span', { class: 'muted' }, 'Open slot: share the link')));
    }
    const me = room.players.find((p) => p.id === state.pid);
    const st = startState(room.players, state.pid);
    for (const [id, b] of modeBtns) {
      const on = room.mode === id;
      b.classList.toggle('btn-on', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
      b.disabled = !st.isHost && !on;
    }
    setText(modeHint, (st.isHost ? 'You pick. Everyone plays it.' : `${MODES[room.mode].name}: the host picks.`)
      + (isPvp(room.mode) ? ' Friendly fire is on: mind your teammate.' : ''));
    diffRow.hidden = isPvp(room.mode);
    for (const [d, b] of diffBtns) {
      const on = room.difficulty === d;
      b.classList.toggle('btn-on', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
      b.disabled = !st.isHost && !on;
    }
    setText(diffHint, DIFFICULTY[room.difficulty].blurb + (st.isHost ? '' : ' The host picks.'));
    setText(readyBtn, me?.ready ? 'Ready (click to undo)' : 'Ready up');
    readyBtn.classList.toggle('btn-on', me?.ready === true);
    startBtn.hidden = !st.isHost;
    startBtn.disabled = !st.canStart;
    if (st.isHost) {
      setText(startHint, st.canStart
        ? (room.players.length === 1 ? 'Solo run: start whenever you like.' : 'Everyone is ready.')
        : `Waiting for: ${st.waitingOn.join(', ')}`);
    } else {
      const host = room.players.find((p) => p.host);
      setText(startHint, host ? `${host.name} starts the match once everyone is ready.` : 'Waiting for a host.');
    }
  }

  const unsubscribe = state.subscribe((vm) => {
    const visible = !suppressed && vm.room !== null && vm.phase === 'lobby';
    if (root.hidden !== !visible) root.hidden = !visible;
    if (!visible || vm.room === null) return;
    if (vm.room !== lastRoom || vm.phase !== lastPhase || state.pid !== lastPid) {
      lastRoom = vm.room;
      lastPhase = vm.phase;
      lastPid = state.pid;
      render(vm.room);
    }
  });

  root.hidden = true;
  return {
    show(): void { suppressed = false; },
    hide(): void { suppressed = true; root.hidden = true; },
    dispose(): void {
      unsubscribe();
      clear(root);
    },
  };
}
